'use strict';

export class PushNotificationsService {
	static $inject = [
		'$rootScope',
		'mx.shell.AuthService',
		'mx.shell.UserProfileService',
		'mx.shell.EventService',
		'mx.SolutionBuilderAgent.Http',
		'mx.shell.ToolbarManager',
		'mx.SolutionBuilderAgent.ContentWidgetService',
		'mx.shell.Config',
		'mx.SolutionBuilderAgent.EntityService',
		'mx.shell.AuthBehaviorProviderService',
	];

	constructor($rootScope, authService, userProfileService, eventService, http, toolbarManager, contentWidgetService, mxConfig, entityService, authBehaviorProviderService) {
		Object.assign(this, {
			$rootScope,
			authService,
			userProfileService,
			eventService,
			http,
			toolbarManager,
			contentWidgetService,
			mxConfig,
			entityService,
			authBehaviorProviderService,
		});

		this.unreadNotifications = 0;
		this.notifications = [];
		this._isPushHubEnabled = false;
		this._userTags = [];

		this._notificationsUpdatedHandlers = [];

		this._serviceWorkerScript = 'workers/push-notifications-sw.js';
		this._serviceWorkerScope = './';
	}

	isSupported() {
		return 'serviceWorker' in navigator && 'Notification' in window && 'PushManager' in window;
	}

	isActivated() {
		return this.isSupported() && this.hasPermissions();
	}

	isDenied() {
		return window.Notification?.permission === 'denied';
	}

	isRequestPermissionsNeeded() {
		return this.isSupported() && this._isPushHubEnabled && !this.isActivated() && !this.isDenied();
	}

	isEnabledInProfile() {
		return this._userTags.length > 0;
	}

	isEnabledByUser() {
		return this.hasPermissions() && this.isEnabledInProfile();
	}

	hasPermissions() {
		return window.Notification?.permission === 'granted';
	}

	async activate() {
		let hasPermissions = this.hasPermissions();
		if (!hasPermissions) {
			hasPermissions = (await window.Notification.requestPermission()) === 'granted';
		}

		if (hasPermissions) {
			await this._initPushNotifications();
		}

		return hasPermissions;
	}

	async init() {
		try {
			this._profile = await this.userProfileService.get();
			this._sessionExpirationTime = await this.authService.getSessionExpirationTime();
			this._pushHubConfig = await this._loadPushHubConfig();
		} catch (error) {
			return;
		}

		this._isPushHubEnabled = this._pushHubConfig && typeof this._pushHubConfig === 'object';

		if (!this.isSupported()) {
			if (this._isPushHubEnabled) {
				this._userTags = await this._getUserTags(this._profile.id);
			}
			await this._initUI();
			return;
		}

		this.authBehaviorProviderService.onBeforeSessionDestroy(() => this._unsubscribe());
		this.authBehaviorProviderService.onSessionExpired(() => this._unsubscribe());

		this.eventService.subscribeGlobal(mx.SolutionBuilderAgent.Events.ObjectDataChanged, async (e, { view }) => {
			if (!view) {
				return;
			}
			const widgetId = this.contentWidgetService.getWidgetIdFromView(view);
			if (this._profile.profileDialogId === widgetId) {
				await this._checkSubscription(true);
			}
		});

		this.sw = await this._initServiceWorker();
		this._subscribeToWorkerMessages();
		await this._initPushHub();
	}

	async _checkSubscription(forceTagsReload = false) {
		if (this._subscribedUserId && (this._subscribedUserId !== this._profile.id || !this._isPushHubEnabled)) {
			await this._unsubscribe();
		}

		if (this._isPushHubEnabled) {
			if (forceTagsReload || this._subscribedUserId !== this._profile.id) {
				this._userTags = await this._getUserTags(this._profile.id);
			}
			if (!this.isEnabledByUser() && this._subscribedUserId) {
				await this._unsubscribe();
			}

			if (this.isEnabledByUser() && (!this._subscribedUserId || forceTagsReload)) {
				await this._subscribe(this._profile.id, this._userTags);
			}
		}

		await this._initUI();
	}

	async _getNotifications() {
		return (
			(await this._sendMessage(
				{
					type: 'get-notifications',
				},
				true
			)) || []
		);
	}

	addNotificationsUpdatedHandler(handler) {
		return this._addHandler(handler, this._notificationsUpdatedHandlers);
	}

	async removeAllNotifications() {
		return this._sendMessage({
			type: 'remove-all-notifications',
		});
	}

	async removeNotification(id) {
		return this._sendMessage({
			type: 'remove-notification',
			value: id,
		});
	}

	async setNotificationsRead() {
		return this._sendMessage({
			type: 'notifications-read',
		});
	}

	_addHandler(handler, handlersArray = []) {
		if (typeof handler !== 'function') {
			console.error('handler is not a function');
			return;
		}

		handlersArray.push(handler);

		return () => {
			const index = handlersArray.indexOf(handler);
			if (index >= 0) {
				handlersArray.splice(index, 1);
			}
		};
	}

	_subscribeToWorkerMessages() {
		navigator.serviceWorker.addEventListener('message', (event) => {
			const data = event.data;
			switch (data.type) {
				case 'notifications_updated':
					this._onNotificationsUpdated(event.data.value);
					break;
				case 'push_hub_ready':
					this._initPushNotifications(event.data.value.subscribedUserId, event.data.value.subscribedUserTags);
					break;
				case 'subscription_status':
					this._onSubscriptionStatusUpdated(event.data.value.subscribedUserId, event.data.value.subscribedUserTags);
					break;
			}
		});
	}

	async _initPushHub() {
		this._sendMessage({
			type: 'init_push_hub',
			value: {
				sessionExpirationTime: this._sessionExpirationTime,
				pushHubConfig: this._pushHubConfig,
				basePath: mx.shell.config.basePath,
			},
		});
	}

	async _initPushNotifications(subscribedUserId, subscribedUserTags) {
		this._subscribedUserId = subscribedUserId;
		if (this._subscribedUserId === this._profile.id) {
			this._userTags = subscribedUserTags;
		}
		await this._checkSubscription();

		if (this._isPushHubEnabled && this.isEnabledByUser()) {
			const notifications = await this._getNotifications();
			await this._onNotificationsUpdated(notifications);
		}
	}

	async _onSubscriptionStatusUpdated(subscribedUserId, subscribedUserTags) {
		this._subscribedUserId = subscribedUserId;
		if (this._subscribedUserId === this._profile.id) {
			this._userTags = subscribedUserTags;
		}
		await this._initUI();
	}

	async _initUI() {
		this.toolbarManager.setPushNotificationsEnabled(this._isPushHubEnabled && this.isEnabledInProfile());
		this.$rootScope.$evalAsync();
	}

	async _initServiceWorker() {
		const registerSW = await navigator.serviceWorker.register(this._serviceWorkerScript, {
			scope: this._serviceWorkerScope,
		});
		await navigator.serviceWorker.ready;
		const serviceWorker = registerSW.installing || registerSW.waiting || registerSW.active;
		if (!serviceWorker) {
			console.error('service-worker registration error');
		}

		const waitServiceWorkerActivated = async (serviceWorker) => {
			if (serviceWorker.state !== 'activated') {
				await new Promise((resolve) => {
					serviceWorker.addEventListener('statechange', function (e) {
						if (e.target.state === 'activated') {
							resolve();
						}
					});
				});
			}
		};
		await waitServiceWorkerActivated(serviceWorker);
		if (navigator.serviceWorker.controller !== serviceWorker) {
			serviceWorker.postMessage({ type: 'claim_me' });
			await new Promise((resolve) => {
				navigator.serviceWorker.addEventListener('controllerchange', resolve);
			});
		}
		navigator.serviceWorker.addEventListener('controllerchange', async () => {
			await waitServiceWorkerActivated(navigator.serviceWorker.controller);
			this.sw = navigator.serviceWorker.controller;
			await this._initPushHub();
		});
		return serviceWorker;
	}

	async _subscribe(userId, userTags) {
		const subscribed = await this._sendMessage(
			{
				type: 'subscribe',
				value: {
					userId: userId,
					userTags: userTags,
				},
			},
			true
		);
		if (subscribed) {
			this._subscribedUserId = userId;
		}
		return subscribed;
	}

	async _unsubscribe() {
		await this._sendMessage(
			{
				type: 'unsubscribe',
			},
			true
		);
		this._subscribedUserId = null;
	}

	async _sendMessage(message, waitForAnswer = false) {
		if (!this.sw) {
			return;
		}
		const messageChannel = new MessageChannel();

		const waitPromise = waitForAnswer
			? new Promise((resolve, reject) => {
					messageChannel.port1.onmessage = (event) => {
						if (event.data.error) {
							reject(event.data.error);
						} else {
							resolve(event.data);
						}
					};
			  })
			: Promise.resolve();

		this.sw.postMessage(message, [messageChannel.port2]);

		return waitPromise;
	}

	async _onNotificationsUpdated(notifications) {
		this.notifications = await this._prepareNotifications(notifications);
		this.unreadNotifications = this.notifications.reduce((unread, notification) => unread + (notification.read ? 0 : 1), 0);
		this._notificationsUpdatedHandlers.forEach((handler) => handler(this.notifications));
	}

	async _prepareNotifications(notifications) {
		const notificationTypes = await this._getNotificationTypes();

		notifications.forEach((notification) => {
			const type = notificationTypes[notification.event] || {};

			Object.assign(notification, {
				color: (type.color || '').replace(/^0xff/i, '#') || '#0399d7',
				icon: type.icon || 'message',
			});
		});

		notifications.sort((a, b) => b.received - a.received);

		return notifications;
	}

	async _getNotificationTypes() {
		if (this._notificationsTypes) {
			return this._notificationsTypes;
		}
		const picker = await this.entityService.getEnumeration('SPSPushNotificationEventPickupType');
		this._notificationsTypes = (picker.Values || []).reduce((aggregator, value) => {
			aggregator[value.InternalName] = {
				color: value.Color,
				icon: value.Icon,
			};
			return aggregator;
		}, {});
		return this._notificationsTypes;
	}

	async _getUserTags(userId) {
		const userTags = await this.http.get(`api/pushnotifications/tags/${userId}`);
		if (userTags.join(',').length >= 8000) {
			console.error('There are too many tags for push subscription. Azure supports not more than 8000 symbols');
		}
		return userTags;
	}

	async _loadPushHubConfig() {
		return this.http.get(`/api/pushNotifications/pushServiceInfo`);
	}
}
